import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Spin, Empty, Button, Card } from 'antd';
import { ReloadOutlined, InfoCircleOutlined, WarningOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import alertsService, {
  type EventAlert,
  type AlertSeverity,
  type AlertType,
  ALERT_TYPE_CONFIG,
  SEVERITY_CONFIG,
  canRoleViewAlert,
} from '../../api/alertsService';
import { useAuth } from '../../hooks/useAuth';
import type { EventWithCircuit } from '../../api/eventsService';

dayjs.extend(utc);
dayjs.extend(timezone);

interface MobileAlertsViewProps {
  event: EventWithCircuit;
  theme: 'light' | 'dark';
}

function getSeverityIcon(severity: AlertSeverity) {
  switch (severity) {
    case 'info':
      return <InfoCircleOutlined style={{ color: SEVERITY_CONFIG.info.color }} />;
    case 'warning':
      return <WarningOutlined style={{ color: SEVERITY_CONFIG.warning.color }} />;
    case 'critical':
      return <ExclamationCircleOutlined style={{ color: SEVERITY_CONFIG.critical.color }} />;
    default:
      return null;
  }
}

function useEventAlerts(event: EventWithCircuit | null) {
  const { authState } = useAuth();
  const userRole = authState.user?.role || '';
  const [alerts, setAlerts] = useState<EventAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!event || event.legacy) {
      setAlerts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await alertsService.getEventAlerts(
        event.season_year,
        event.circuit_id,
        event.start_date,
        event.demo ?? false
      );

      if (response.success && response.data) {
        const fetched = response.data.filter((a) => !a.is_deleted && canRoleViewAlert(a, userRole));
        setAlerts(fetched);
      } else {
        setAlerts([]);
      }
    } catch (err) {
      console.error('Error fetching mobile alerts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load alerts');
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [event, userRole]);

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, 60 * 1000);
    return () => clearInterval(intervalId);
  }, [fetchData]);

  return { alerts, loading, error, refresh: fetchData };
}

export const MobileAlertsView: React.FC<MobileAlertsViewProps> = ({ event, theme }) => {
  const { alerts, loading, error, refresh } = useEventAlerts(event);
  const isDark = theme === 'dark';
  const tz = event.circuit?.time_zone || 'UTC';

  const sortedAlerts = useMemo(() => {
    return [...alerts].sort(
      (a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime()
    );
  }, [alerts]);

  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) return '—';
    return dayjs(timestamp).tz(tz).format('D MMM HH:mm');
  };

  if (event.legacy) {
    return (
      <div
        style={{
          padding: 24,
          textAlign: 'center',
          color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.55)',
        }}
      >
        <Empty description="Alerts are not available for legacy events. View the Messages page for legacy event notifications." />
      </div>
    );
  }

  if (loading && alerts.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          gap: 16,
        }}
      >
        <Spin size="large" />
        <span style={{ color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.55)' }}>
          Loading alerts…
        </span>
      </div>
    );
  }

  if (error && alerts.length === 0) {
    return (
      <div
        style={{
          padding: 24,
          textAlign: 'center',
          color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)',
        }}
      >
        <p style={{ marginBottom: 12 }}>{error}</p>
        <Button icon={<ReloadOutlined />} onClick={refresh}>
          Retry
        </Button>
      </div>
    );
  }

  if (alerts.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <Empty description="No alerts for this event" />
      </div>
    );
  }

  const cardBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
  const cardBorder = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        padding: 16,
      }}
    >
      {/* Header - always visible */}
      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <span style={{ fontSize: 13, color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)' }}>
          {alerts.length} alert{alerts.length !== 1 ? 's' : ''}
        </span>
        <Button type="text" icon={<ReloadOutlined />} onClick={refresh} size="small">
          Refresh
        </Button>
      </div>

      {/* Scrollable alert cards */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {sortedAlerts.map((alert) => {
          const typeConfig = ALERT_TYPE_CONFIG[alert.alert_type as AlertType];
          const severityConfig = SEVERITY_CONFIG[alert.severity];
          return (
            <Card
              key={alert.id}
              size="small"
              style={{ backgroundColor: cardBg, borderColor: cardBorder, borderRadius: 10 }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {/* Severity + Type row */}
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 12,
                      color: severityConfig?.color ?? undefined,
                    }}
                  >
                    {getSeverityIcon(alert.severity)}
                    {severityConfig?.label ?? alert.severity}
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)',
                    }}
                  >
                    {typeConfig?.icon} {typeConfig?.label ?? alert.alert_type}
                  </span>
                </div>

                {/* Title */}
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 600,
                    color: isDark ? '#fff' : '#000',
                  }}
                >
                  {alert.title || '—'}
                </div>

                {/* Message */}
                {alert.message && (
                  <div
                    style={{
                      fontSize: 14,
                      color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)',
                      lineHeight: 1.4,
                    }}
                  >
                    {alert.message}
                  </div>
                )}

                {/* Sessions */}
                {alert.sessions && alert.sessions.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 4,
                    }}
                  >
                    {alert.sessions.map((s) => (
                      <span
                        key={s}
                        style={{
                          fontSize: 11,
                          padding: '2px 6px',
                          borderRadius: 4,
                          backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
                          color: isDark ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.65)',
                        }}
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                )}

                {/* Times */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    fontSize: 12,
                    color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)',
                  }}
                >
                  <span>Start: {formatTimestamp(alert.start_time)}</span>
                  {alert.end_time && (
                    <span>End: {formatTimestamp(alert.end_time)}</span>
                  )}
                </div>

                {/* Created by */}
                <div
                  style={{
                    fontSize: 11,
                    color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)',
                  }}
                >
                  {alert.is_automated ? '🤖 ' + alert.created_by : alert.created_by}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
